import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import type { ShopifyScopeStatus } from '../services/integrationApi';

export interface ShopifyRedirectPermissionAlertProps {
  scopeStatus?: ShopifyScopeStatus | null;
  /** How the store's token was obtained: Motzu's app, the store's own app, or a pasted Admin API token. */
  installMethod: 'public_oauth' | 'custom_app_oauth' | 'manual_token';
  /** The merge tool (flag `variant_listing_consolidation`) is on for the organization. */
  mergeEnabled: boolean;
  canManageCredentials: boolean;
  adminOnlyLabel: string;
  reconnecting: boolean;
  checking: boolean;
  onReconnect: () => void;
  onCheckPermissions: () => void;
}

/**
 * MOT-601: a listing merge redirects the old product URLs only with Shopify's `write_content`
 * scope. Every sync works without it, so it is not part of `needsReauth`; this prompt is shown
 * only where merges can run, and only while no catalog prompt is already on the card.
 * Check permissions re-reads the grant, which is how a store on its own app or a pasted token
 * clears the prompt after adding the scope in Shopify.
 */
const ShopifyRedirectPermissionAlert: React.FC<ShopifyRedirectPermissionAlertProps> = ({
  scopeStatus,
  installMethod,
  mergeEnabled,
  canManageCredentials,
  adminOnlyLabel,
  reconnecting,
  checking,
  onReconnect,
  onCheckPermissions,
}) => {
  if (!mergeEnabled || scopeStatus?.needsReauth !== false || scopeStatus.redirectsMissing !== true) return null;
  const ownApp = installMethod !== 'public_oauth';
  return (
    <Alert
      severity="info"
      sx={{ mt: 1.5, py: 0.25, alignItems: 'center' }}
      action={
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {canManageCredentials ? (
            <Button size="small" color="inherit" onClick={onReconnect} disabled={reconnecting}>
              {reconnecting ? 'Opening Shopify…' : ownApp ? 'How to reconnect' : 'Reconnect'}
            </Button>
          ) : (
            <Typography variant="caption" sx={{ alignSelf: 'center' }}>
              {adminOnlyLabel}
            </Typography>
          )}
          <Button size="small" color="inherit" onClick={onCheckPermissions} disabled={checking}>
            {checking ? 'Checking…' : 'Check permissions'}
          </Button>
        </Box>
      }
    >
      <strong>Reconnect to redirect merged listings.</strong> When colour or size listings are merged, Motzu needs
      Shopify's permission to manage URL redirects (write_content) so the old product pages send shoppers to the merged
      listing.
      {ownApp && (
        <> This store uses its own Shopify app: add write_content to that app's access scopes and release it, then reconnect or check permissions.</>
      )}
    </Alert>
  );
};

export default ShopifyRedirectPermissionAlert;
